// Lib
import React, { useCallback, useMemo } from 'react';

// Components
import TitleTiptapEditor from '../titleEditor/TitleTiptapEditor';
import { useGroupTranslator } from '../../components/i18n/GroupTranslationContext';
import { useElementTiptapContent } from '../../../common/tiptap/conversion/elementConversion/useElementTiptapContent';

// Utils
import { getElementId, getTitle } from '../../../common/elements/utils/elementPropertyUtils';
import { getStartsOfSelectedTextLines } from '../../../common/tiptap/extensions/textIndentation/indentationUtils';

// Types
import type { TitleTiptapEditorProps } from '../titleEditor/TitleTiptapEditor';
import type { ImMNElement } from '../../../common/elements/elementModelTypes';
import { Command } from '@tiptap/core';
import { ELEMENT_CREATION_SOURCES } from '../../../common/elements/elementConstants';
import { DocumentType } from '../../../common/tiptap/conversion/elementConversion/elementConversion';

export type TableTitleEditorProps = {
    element: ImMNElement;
    isEditable: boolean;
    isEditing: boolean;
    isSingleSelected: boolean;
    spellCheck: boolean;
    currentEditorId: string;
    startEditing: TitleTiptapEditorProps['startEditing'];
    onChange: TitleTiptapEditorProps['onChange'];
    dispatchUpdateTitle: TitleTiptapEditorProps['dispatchUpdateTitle'];
    dispatchHideTitle: (args: { id: string }) => void;
    createNewElement: (args: { creationSource: string }) => void;
    jumpToFirstCell: () => void;
    submitTitle: () => void;
};

const TableTitleEditor = React.forwardRef<HTMLDivElement, TableTitleEditorProps>(function TableTitleEditor(props, ref) {
    const {
        element,
        dispatchHideTitle,
        jumpToFirstCell,
        isEditable,
        isEditing,
        isSingleSelected,
        spellCheck,
        currentEditorId,
        startEditing,
        dispatchUpdateTitle,
        onChange,
        createNewElement,
        submitTitle,
    } = props;

    const elementId = getElementId(element);
    const title = getTitle(element);

    const tiptapContent = useElementTiptapContent(title, elementId, DocumentType.textContent);

    const groupTranslate = useGroupTranslator();
    const hideTitleAndGoToFirstCell = useCallback(() => {
        dispatchHideTitle({ id: elementId });
        jumpToFirstCell();
    }, [elementId, dispatchHideTitle, jumpToFirstCell]);

    /* --- Keyboard Shortcuts --- */

    const onRightArrow: Command = useCallback(
        ({ state }) => {
            const { selection } = state;

            if (!selection.empty) return false;
            if (selection.$from.parentOffset !== selection.$from.parent.content.size) return false;

            jumpToFirstCell();
            return true;
        },
        [jumpToFirstCell],
    );

    const onDownArrow: Command = useCallback(
        ({ state, editor }) => {
            const { selection } = state;

            if (!selection.empty) return false;

            // Only jump if on the last line
            const text = editor.getText({ blockSeparator: '\n' });
            const lineStarts = getStartsOfSelectedTextLines(text, 0, 0, state.doc.content.size);

            const lastLineStart = lineStarts.slice(-1)[0];

            if (selection.from < lastLineStart.pos) return false;

            jumpToFirstCell();
            return true;
        },
        [jumpToFirstCell],
    );

    const onTab: Command = useCallback(() => {
        jumpToFirstCell();
        return true;
    }, [jumpToFirstCell]);

    const onModReturn: Command = useCallback(() => {
        createNewElement({ creationSource: ELEMENT_CREATION_SOURCES.CMD_ENTER });
        return true;
    }, [createNewElement]);

    const onReturn: Command = useCallback(() => {
        submitTitle();
        return true;
    }, [submitTitle]);

    const keyboardShortcuts = useMemo(
        () => ({
            'Mod-Enter': onModReturn,
            Enter: onReturn,
            Tab: onTab,
            ArrowRight: onRightArrow,
            ArrowDown: onDownArrow,
        }),
        [onModReturn],
    );

    return (
        <div className="TableTitleEditor" ref={ref}>
            <TitleTiptapEditor
                placeholder={groupTranslate('title.placeholder')}
                onEmptyDelete={hideTitleAndGoToFirstCell}
                element={element}
                title={tiptapContent}
                isEditable={isEditable}
                isEditing={isEditing}
                isSingleSelected={isSingleSelected}
                spellCheck={spellCheck}
                currentEditorId={currentEditorId}
                startEditing={startEditing}
                dispatchUpdateTitle={dispatchUpdateTitle}
                onChange={onChange}
                keyboardShortcuts={keyboardShortcuts}
            />
        </div>
    );
});

export default TableTitleEditor;
